import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Info, Rocket } from 'lucide-react';

import FormField from '../../../elements/FormField';
import LocationSearch from '../../../elements/LocationSelector';
import LocationList from '../../../elements/LocationList';
import { generateMarketName } from '../../../utils/naming';
import { getAggregatedPricing } from '../../../utils/common';
import { useAppDispatch } from '../../../hooks/useAppDipatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { setCreationStep } from '../../../features/campaigns/campaignsSlice';

import type {
  CampaignCreationStep,
  MarketFormData,
  Campaign,
  ProductType,
  Target,
} from '../../../types/campaign';

interface MarketCreationFormProps {
  defaultValues: MarketFormData | null;
  campaign: Campaign;
  onSubmit: (marketData: MarketFormData) => void;
  isEdit?: boolean;
  onCancel?: () => void;
}

const initialMarketData: MarketFormData = {
  campaignID: '',
  name: '',
  targeting: [],
  parentBid: '250',
  subtypeBids: {},
  callHours: null,
};

export default function MarketCreationForm({
  defaultValues,
  campaign,
  onSubmit,
  isEdit = false,
  onCancel,
}: MarketCreationFormProps) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [previousStep, setPreviousStep] = useState<CampaignCreationStep>('campaign');
  const [formData, setFormData] = useState<MarketFormData>(defaultValues ?? initialMarketData);
  const [subtypes, setSubtypes] = useState<string[]>([]);
  const [productType, setProductType] = useState<ProductType>('leads');
  const [bidMode, setBidMode] = useState<'smart' | 'manual'>(
    isEdit && defaultValues?.subtypeBids && Object.keys(defaultValues.subtypeBids).length > 0
      ? 'manual'
      : 'smart',
  );
  const [isParentBidManual, setIsParentBidManual] = useState(false);
  const [isNameManual, setIsNameManual] = useState(isEdit);

  const { campaignObjective, pricingConfig } = useAppSelector((state) => state.campaigns);

  useEffect(() => {
    const matchingObjective = campaignObjective.find(
      (obj: any) => obj.claim_type === campaign.claim_type,
    );

    const newSubtypes = matchingObjective ? matchingObjective.claim_subtypes : [];

    setSubtypes(newSubtypes);
    setProductType(matchingObjective.result_type);
  }, [campaign, campaignObjective]);

  const parentPricing = useMemo(() => {
    if (!pricingConfig) return { floor: 0, suggested: 0, tier: 'D', stateCode: 'D' };
    return getAggregatedPricing(pricingConfig, formData.targeting, productType, 'Standard MVA');
  }, [formData.targeting, productType, pricingConfig]);

  const subtypePricingMap = useMemo(() => {
    if (!pricingConfig) return {};
    return subtypes.reduce(
      (acc, st) => {
        acc[st] = getAggregatedPricing(pricingConfig, formData.targeting, productType, st);
        return acc;
      },
      {} as Record<string, any>,
    );
  }, [formData.targeting, productType, subtypes, pricingConfig]);

  const errors = useMemo(() => {
    const errs: Record<string, string> = {};
    const pBid = Number(formData.parentBid);

    if (!formData.parentBid || pBid < parentPricing.floor) {
      errs.parent = `Minimum bid for ${parentPricing.stateCode} is $${parentPricing.floor}`;
    }

    if (bidMode === 'manual') {
      subtypes.forEach((st) => {
        const pricing = subtypePricingMap[st];
        const currentInput = formData.subtypeBids[st];
        if (!currentInput || currentInput === '') {
          errs[st] = `${st} bid is required in Manual mode.`;
        } else if (Number(currentInput) < pricing.floor) {
          errs[st] = `Minimum bid for ${st} in ${pricing.stateCode} is $${pricing.floor}`;
        }
      });
    }
    return errs;
  }, [formData.parentBid, formData.subtypeBids, bidMode, parentPricing, subtypePricingMap]);

  useEffect(() => {
    const newSubtypeBids = { ...formData.subtypeBids };
    let hasChanges = false;

    subtypes.forEach((st) => {
      const pricing = subtypePricingMap[st];
      if (!pricing) return;

      const currentBidValue = formData.subtypeBids[st];
      const currentBidNum = Number(currentBidValue);

      if (bidMode === 'smart' || !currentBidValue || currentBidNum < pricing.floor) {
        newSubtypeBids[st] = pricing.suggested.toString();
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setFormData((prev) => ({
        ...prev,
        subtypeBids: newSubtypeBids,
      }));
    }
  }, [subtypePricingMap, bidMode, subtypes]);

  useEffect(() => {
    const currentBid = Number(formData.parentBid);

    if (!isParentBidManual || currentBid < parentPricing.floor) {
      setFormData((prev) => ({
        ...prev,
        parentBid: parentPricing.suggested.toString(),
      }));

      if (currentBid < parentPricing.floor) {
        setIsParentBidManual(false);
      }
    }
  }, [parentPricing.suggested, parentPricing.floor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const finalSubtypeBids = { ...formData.subtypeBids };

    if (bidMode === 'smart') {
      subtypes.forEach((st) => {
        finalSubtypeBids[st] = subtypePricingMap[st].suggested.toString();
      });
    }
    onSubmit({
      ...formData,
      subtypeBids: finalSubtypeBids,
      campaignID: campaign.id,
      activateImmediately: true,
    });
  };

  const isValid = () => {
    if (!formData.name) return false;

    return Object.keys(errors).length === 0;
  };

  const onBackHandle = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    if (previousStep === 'campaign') {
      dispatch(setCreationStep('campaign'));
    } else {
      dispatch(setCreationStep('none'));
    }
  };

  useEffect(() => {
    if (location.pathname.includes(campaign.id)) {
      setPreviousStep('none');
    }
  }, [location]);

  useEffect(() => {
    if (!isNameManual) {
      const defaultName = generateMarketName(formData.targeting);
      setFormData((prev) => ({ ...prev, name: defaultName }));
    }
  }, [formData.targeting, isNameManual]);

  useEffect(() => {
    if (defaultValues) {
      setFormData(defaultValues);
    }
  }, [defaultValues]);

  return (
    <div className="glass-card rounded-xl">
      <div className="border-b border-gray-700/50 p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-semibold text-neutral-light">
          {isEdit ? 'Edit Market' : 'Create Market'}
        </h2>
        <p className="mt-2 text-sm sm:text-base text-text-muted">
          Define your geographic targeting, bid strategy, and specific claim type settings.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6 sm:space-y-8">
        <section>
          <h3 className="text-lg font-semibold text-neutral-light mb-4">Basic Information</h3>
          <FormField
            label="Market Name"
            required
            highlight
            defaultValue={generateMarketName(formData.targeting)}
          >
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                setIsNameManual(true);
              }}
              className="w-full px-4 py-3 bg-gray-700/50 border-2 border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50 focus:ring-2 focus:ring-primary-500/20"
              placeholder={`e.g. - US`}
            />
          </FormField>
        </section>

        <section>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0 mb-4">
            <h3 className="text-lg font-semibold text-neutral-light">Geographic Targeting</h3>
            <span className="text-xs text-text-muted">Optional - Defaults to All US</span>
          </div>
          <div className="space-y-6">
            <LocationSearch
              onSelect={(location: Target) =>
                setFormData({
                  ...formData,
                  targeting: [...formData.targeting, location],
                })
              }
              selectedTargets={formData.targeting}
            />
            <LocationList
              locations={formData.targeting}
              onRemove={(id: string) =>
                setFormData({
                  ...formData,
                  targeting: formData.targeting.filter((loc) => loc.id !== id),
                })
              }
            />
          </div>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neutral-light mb-4">Bid Settings</h3>
          <FormField label="Parent Bid" required highlight>
            <input
              type="number"
              value={formData.parentBid}
              onChange={(e) => {
                setFormData({ ...formData, parentBid: e.target.value });
                setIsParentBidManual(true);
              }}
              className={`w-full px-4 py-3 bg-gray-700/50 border-2 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none transition-colors 
                ${errors.parent ? 'border-red-500/50 focus:border-red-500' : 'border-gray-700/50 focus:border-accent/50'}`}
              placeholder={`e.g. ${parentPricing.floor}`}
            />
            <p className="mt-2 text-sm text-text-muted">
              Min: ${parentPricing.floor} • Suggested: ${parentPricing.suggested}
            </p>
            {errors.parent && (
              <p className="mt-1 text-xs text-red-400 font-semibold">{errors.parent}</p>
            )}
          </FormField>

          {subtypes.length > 0 && (
            <div className="mt-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0 mb-1">
                <h4 className="text-md font-semibold text-neutral-light">Claim Type Bids</h4>
                <div className="flex items-center bg-brand-surface/50 rounded-lg p-1 text-xs sm:text-sm">
                  <button
                    type="button"
                    onClick={() => setBidMode('smart')}
                    className={`px-2 sm:px-3 py-1 rounded-md transition-colors ${
                      bidMode === 'smart'
                        ? 'bg-accent text-brand-bg'
                        : 'text-text-muted hover:text-neutral-light'
                    }`}
                  >
                    Smart (recommended)
                  </button>
                  <button
                    type="button"
                    onClick={() => setBidMode('manual')}
                    className={`px-2 sm:px-3 py-1 rounded-md transition-colors ${
                      bidMode === 'manual'
                        ? 'bg-accent text-brand-bg'
                        : 'text-text-muted hover:text-neutral-light'
                    }`}
                  >
                    Manual
                  </button>
                </div>
              </div>
              <p className="text-sm text-text-muted mb-4 h-5">
                {bidMode === 'smart'
                  ? 'Smart mode: We automatically apply Fairday’s minimums and suggested bids per claim type.'
                  : 'Smart = auto amounts, Manual = edit.'}
              </p>
              <div className="border border-gray-700/50 rounded-lg bg-gray-900/20">
                <div className="flex justify-between items-center px-3 sm:px-4 py-2 border-b border-gray-700/50">
                  <span className="text-xs sm:text-sm font-medium text-gray-400">Claim type</span>
                  <span className="text-xs sm:text-sm font-medium text-gray-400">
                    {bidMode === 'smart' ? 'Smart bid' : 'Manual bid'}
                  </span>
                </div>
                <div>
                  {subtypes.map((subtype, index) => {
                    const pricing = subtypePricingMap[subtype];
                    const hasError = errors[subtype];

                    return (
                      <div key={index} className="px-3 sm:px-4 py-3">
                        <div
                          key={subtype}
                          className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0 border-gray-800/50"
                        >
                          <span className="text-sm sm:text-base text-neutral-light">{subtype}</span>
                          {bidMode === 'smart' ? (
                            <span className="text-neutral-light font-semibold">${pricing.suggested}</span>
                          ) : (
                            <div className="relative flex items-center">
                              <span className="absolute left-3 text-text-muted pointer-events-none">
                                $
                              </span>
                              <input
                                type="number"
                                value={formData.subtypeBids[subtype] || ''}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    subtypeBids: {
                                      ...formData.subtypeBids,
                                      [subtype]: e.target.value,
                                    },
                                  })
                                }
                                className={`w-full sm:w-28 pl-6 pr-2 py-2 bg-gray-700/50 border-2 rounded-lg text-gray-100 text-right focus:outline-none
                                  ${hasError ? 'border-red-500/50 focus:border-red-500' : 'border-gray-700/50 focus:border-accent/50'}`}
                                placeholder={`${pricing.suggested}`}
                              />
                            </div>
                          )}
                        </div>
                        <div className="mt-1">
                          <p className="text-xs text-text-muted">
                            Min: ${pricing.floor} • Suggested: ${pricing.suggested}
                          </p>
                          {bidMode === 'manual' && hasError && (
                            <p className="mt-1 text-xs text-red-400 font-semibold">{hasError}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {/* {subtypes.length > 0 && (
            <div className="mt-6">
              <h4 className="text-md font-semibold text-neutral-light mb-2">Claim Subtypes</h4>
              <p className="text-sm text-text-muted mb-4">
                Default bid is **${formData.parentBid}**. You may override for specific
                subtypes.
              </p>
              <div className="space-y-4">
                {subtypes.map((subtype, index) => (
                  <FormField key={index} label={subtype}>
                    <input
                      type="number"
                      value={formData.subtypeBids[subtype] || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subtypeBids: {
                            ...formData.subtypeBids,
                            [subtype]: e.target.value,
                          },
                        })
                      }
                      className="w-full px-4 py-3 bg-gray-700/50 border-2 border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50"
                      placeholder={`Inherited: $${formData.parentBid}`}
                    />
                  </FormField>
                ))}
              </div>
            </div>
          )} */}
        </section>

        {campaign.objective === 'call' && (
          <section>
            <h3 className="text-lg font-semibold text-neutral-light mb-4">Call Availability</h3>
            <p className="text-sm text-text-muted mb-4">
              Default: **8am–8pm local**. You may override these hours for this market.
            </p>
            {/* Input fields for start/end times would go here. For simplicity, we'll use a placeholder. */}
            <div className="p-4 bg-brand-hover rounded-lg text-text-muted">
              Time selection controls will be implemented here.
            </div>
          </section>
        )}

        <div className="pt-6 border-t border-gray-700/50 space-y-4">
          {!isEdit && (
            <div className="flex items-start sm:items-center gap-2 px-3 sm:px-4 py-3 bg-brand-surface/50 border border-yellow-600/30 rounded-lg">
              <Info className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5 sm:mt-0" />
              <span className="text-xs sm:text-sm text-yellow-500">
                <span className="font-medium">Note:</span> Campaigns are eligible for delivery instantly when the market goes live.
              </span>
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <button
              type="button"
              onClick={onBackHandle}
              className="order-last sm:order-first px-6 py-2 text-sm text-text-muted hover:text-white transition-colors"
            >
              Back to Campaign Setup
            </button>
            {campaign.status === 'active' ? (
              <button
                type="submit"
                disabled={!isValid()}
                className="group flex items-center justify-center gap-2 px-6 py-2.5 sm:py-2 bg-accent hover:bg-accent-hover hover:brightness-110 disabled:bg-gray-700 disabled:cursor-not-allowed text-brand-bg font-medium rounded-lg transition-all"
              >
                {isEdit ? 'Save Changes' : 'Launch Market'}
                {!isEdit && (
                  <Rocket className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                )}
              </button>
            ) : (
              <button
                type="submit"
                disabled={!isValid()}
                className="group flex items-center justify-center gap-2 px-6 py-2.5 sm:py-2 bg-accent hover:bg-accent-hover hover:brightness-110 disabled:bg-gray-700 disabled:cursor-not-allowed text-brand-bg font-medium rounded-lg transition-all"
              >
                {isEdit ? 'Save Changes' : 'Launch Campaign'}
                {!isEdit && (
                  <Rocket className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                )}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
